import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { CalendarCheck, ChevronDown, Menu, Minus, Phone, Plus, Search, X } from 'lucide-react';
import { conditionLabel, conditionMatchesQuery, getAllConditions, getGroupedConditions } from '../data/conditions/index';
import { getConditionUrl } from '../data/conditionUrls';
import type { ConditionCategory } from '../data/conditions/types';
import SocialLinks from './SocialLinks';
import { SITE } from '../data/site';

/**
 * C1 header: utility row (Blog, Contact, Call) above the nav row (logo, menu
 * with dropdowns + conditions mega panel with search, search icon, outline
 * secondary button, solid Book Online). Sticky; shrinks and gains a hairline
 * after ~80px; frosted over photo heroes (chrome.css). Under 1024px: logo +
 * booking icon + hamburger; full-width panel with accordion sub-menus, body
 * scroll lock, Esc / backdrop close, focus trap.
 *
 * App.tsx renders <Header.UtilityBar /> then <Header.Navigation />. The whole
 * header now lives in Navigation; UtilityBar renders nothing (kept so the
 * Layout does not change).
 */

type MenuKey = 'about' | 'team' | 'services' | 'patients' | 'conditions';

interface MenuLink {
  label: string;
  to?: string;
  href?: string;
}

interface MenuGroup {
  key: MenuKey;
  label: string;
  to: string;
  children: MenuLink[];
  activePaths: string[];
}

const MENU: MenuGroup[] = [
  {
    key: 'about',
    label: 'About',
    to: '/about-us/',
    activePaths: [
      '/about-us',
      '/our-awards-and-accolades',
      '/community-talks',
      '/community-outreach',
      '/events',
      '/family-focus',
      '/living-well-naturally',
      '/partner-with-us',
      '/webinars',
      '/sked-app',
      '/newsletter',
    ],
    children: [
      { label: 'About Us', to: '/about-us/' },
      { label: 'Awards & Accolades', to: '/our-awards-and-accolades/' },
      { label: 'Community Talks', to: '/community-talks/' },
      { label: 'Community Outreach', to: '/community-outreach/' },
      { label: 'Events', to: '/events/' },
      { label: 'Family of the Month', to: '/family-focus/' },
      { label: 'Living Well Naturally', to: '/living-well-naturally/' },
      { label: 'Partner With Us', to: '/partner-with-us/' },
      { label: 'Webinars', to: '/webinars/' },
      { label: 'SKED App', to: '/sked-app/' },
      { label: 'Fullscript', href: 'https://us.fullscript.com/welcome/vanevery' },
      { label: 'Newsletter', to: '/newsletter/' },
    ],
  },
  {
    key: 'team',
    label: 'Our Team',
    to: '/meet-the-doctors/',
    activePaths: [
      '/meet-the-doctors',
      '/dr-anna-saylor',
      '/dr-john-johr',
      '/dr-zach-conner',
      '/christina',
      '/meet-heather-massage-therapist',
      '/meet-lou-ann-office-assistant',
      '/tricia',
    ],
    children: [
      { label: 'Meet the Doctors', to: '/meet-the-doctors/' },
      { label: 'Dr. Anna Saylor', to: '/dr-anna-saylor/' },
      { label: 'Dr. John Johr', to: '/dr-john-johr/' },
      { label: 'Dr. Zach Conner', to: '/dr-zach-conner/' },
      { label: 'Christina', to: '/christina/' },
      { label: 'Heather', to: '/meet-heather-massage-therapist/' },
      { label: 'Lou Ann', to: '/meet-lou-ann-office-assistant/' },
      { label: 'Tricia', to: '/tricia/' },
    ],
  },
  {
    key: 'services',
    label: 'Services',
    to: '/our-services/',
    activePaths: ['/our-services', '/our-techniques', '/prenatal-chiropractic', '/pediatric-chiropractic', '/massage-therapy', '/laser-therapy'],
    children: [
      { label: 'Our Services', to: '/our-services/' },
      { label: 'Chiropractic Care', to: '/our-techniques/' },
      { label: 'Pregnancy Chiropractic', to: '/prenatal-chiropractic/' },
      { label: 'Pediatric Chiropractic', to: '/pediatric-chiropractic/' },
      { label: 'Massage Therapy', to: '/massage-therapy/' },
      { label: 'Laser Therapy', to: '/laser-therapy/' },
    ],
  },
  {
    key: 'patients',
    label: 'New Patients',
    to: '/new-patient-center/',
    activePaths: ['/new-patient-center', '/orientation-videos', '/office-tour', '/patient-reviews', '/contact-us'],
    children: [
      { label: 'New Patient Center', to: '/new-patient-center/' },
      { label: 'Orientation Videos', to: '/orientation-videos/' },
      { label: 'Office Tour', to: '/office-tour/' },
      { label: 'Reviews', to: '/patient-reviews/' },
      { label: 'Contact Us', to: '/contact-us/' },
    ],
  },
  {
    key: 'conditions',
    label: 'Conditions',
    to: '/we-can-help/',
    activePaths: ['/we-can-help', '/conditions'],
    children: [],
  },
];

const SECONDARY_CTA = {
  label: 'Perfect Storm Webinar',
  lead: 'Perfect Storm',
  short: 'Webinar',
  to: '/the-perfect-storm/',
};
const STUCK_AT = 80;
const UNSTUCK_AT = 24;
const MOBILE_QUERY = '(max-width: 1023px)';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function normalizePath(path: string) {
  if (path === '/') return path;
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

function MenuAnchor({ item, className }: { item: MenuLink; className?: string }) {
  if (item.href) {
    return (
      <a className={className} href={item.href} target="_blank" rel="noopener noreferrer">
        {item.label}
      </a>
    );
  }
  return (
    <Link className={className} to={item.to ?? '/'}>
      {item.label}
    </Link>
  );
}

/** Kept for App.tsx; the utility row now renders inside Navigation. */
function UtilityBar() {
  return null;
}

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [stuck, setStuck] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState<MenuKey | null>(null);
  const [desktopMenu, setDesktopMenu] = useState<MenuKey | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [panelTop, setPanelTop] = useState<number | null>(null);
  const [focusSearch, setFocusSearch] = useState(false);
  const [desktopConditionCategory, setDesktopConditionCategory] = useState<ConditionCategory>('pediatric');
  const [mobileConditionCategory, setMobileConditionCategory] = useState<ConditionCategory | null>(null);

  // Conditions search: every condition page (same list as We Can Help), with a
  // search box at the top of the panel. Filtering matches title or subtitle;
  // Enter opens the first hit.
  const [conditionQuery, setConditionQuery] = useState('');
  const desktopSearchRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);
  const allConditionGroups = useMemo(() => getGroupedConditions(), []);
  const conditionGroups = useMemo(() => {
    const q = conditionQuery.trim().toLowerCase();
    if (!q) return allConditionGroups;
    return allConditionGroups
      .map((group) => ({
        ...group,
        conditions: group.conditions.filter((c) => conditionMatchesQuery(c, q)),
      }))
      .filter((group) => group.conditions.length > 0);
  }, [conditionQuery, allConditionGroups]);
  const conditionMatchCount = conditionGroups.reduce((n, g) => n + g.conditions.length, 0);
  const activeDesktopConditionGroup =
    allConditionGroups.find((group) => group.category === desktopConditionCategory) ?? allConditionGroups[0];

  // Keep the desktop Conditions mega panel inside the viewport: nudge it left
  // or right if it would spill off-screen, and cap its height so short laptop
  // screens scroll the condition list instead of clipping it.
  useLayoutEffect(() => {
    if (desktopMenu !== 'conditions') return;
    const panel = document.getElementById('conditions-submenu');
    if (!panel) return;
    const EDGE = 16;
    const fit = () => {
      panel.style.setProperty('--mega-dx', '0px');
      const rect = panel.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      let dx = 0;
      if (rect.right > vw - EDGE) dx = vw - EDGE - rect.right;
      if (rect.left + dx < EDGE) dx = EDGE - rect.left;
      panel.style.setProperty('--mega-dx', `${Math.round(dx)}px`);
      panel.style.setProperty('--mega-max-h', `${Math.max(320, Math.floor(window.innerHeight - rect.top - EDGE))}px`);
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('scroll', fit, { passive: true });
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('scroll', fit);
    };
  }, [desktopMenu, stuck]);

  // Close everything on navigation.
  useEffect(() => {
    setMobileOpen(false);
    setMobileAccordion(null);
    setDesktopMenu(null);
    setConditionQuery('');
  }, [location.pathname]);

  // Sticky shrink with hysteresis (rAF-throttled, passive).
  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      setStuck((prev) => (prev ? y > UNSTUCK_AT : y > STUCK_AT));
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Breakpoint (the CSS hides the desktop nav under 1024px; this only keeps
  // state tidy when the viewport crosses the line).
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const apply = () => {
      setIsMobile(mq.matches);
      if (mq.matches) setDesktopMenu(null);
      else {
        setMobileOpen(false);
        setMobileAccordion(null);
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Mobile panel: body scroll lock, Esc, focus trap, panel top under the header.
  useEffect(() => {
    if (!mobileOpen) {
      document.body.classList.remove('ve-menu-open');
      return;
    }
    document.body.classList.add('ve-menu-open');
    const header = headerRef.current;
    const measure = () => {
      if (header) setPanelTop(Math.max(0, Math.round(header.getBoundingClientRect().bottom)));
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMobileOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !header) return;
      const nodes = Array.from(header.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !header.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => {
      const firstLink = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      firstLink?.focus();
    }, 30);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
      cancelAnimationFrame(raf);
      window.clearTimeout(focusTimer);
      document.body.classList.remove('ve-menu-open');
    };
  }, [mobileOpen]);

  // Search icon: open the conditions panel and focus its search box.
  useEffect(() => {
    if (!focusSearch) return;
    const input = isMobile ? mobileSearchRef.current : desktopSearchRef.current;
    if (input) {
      input.focus();
      setFocusSearch(false);
    }
  }, [focusSearch, isMobile, desktopMenu, mobileAccordion, mobileOpen]);

  const openSearch = useCallback(() => {
    if (isMobile) {
      setMobileOpen(true);
      setMobileAccordion('conditions');
    } else {
      setDesktopMenu('conditions');
    }
    setFocusSearch(true);
  }, [isMobile]);

  const currentPath = normalizePath(location.pathname);
  const isActive = (path: string) => currentPath === normalizePath(path);
  const groupActive = (g: MenuGroup) =>
    g.activePaths.some(isActive) ||
    (g.key === 'conditions' && getAllConditions().some((c) => isActive(getConditionUrl(c.slug))));

  const desktopHandlers = (key: MenuKey) => ({
    onMouseEnter: () => {
      if (!isMobile) setDesktopMenu(key);
    },
    onMouseLeave: (event: ReactMouseEvent<HTMLLIElement>) => {
      if (!isMobile && !event.currentTarget.contains(document.activeElement)) setDesktopMenu(null);
    },
    onFocus: () => {
      if (!isMobile) setDesktopMenu(key);
    },
    onBlur: (event: ReactFocusEvent<HTMLLIElement>) => {
      const next = event.relatedTarget as Node | null;
      if (!isMobile && !event.currentTarget.contains(next)) setDesktopMenu(null);
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLLIElement>) => {
      if (event.key === 'Escape' && desktopMenu === key) {
        event.preventDefault();
        // Focus the parent link first (its onFocus opens the menu), then close.
        (event.currentTarget.querySelector('a') as HTMLElement | null)?.focus();
        setDesktopMenu(null);
      }
    },
  });

  const onSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (conditionQuery) setConditionQuery('');
      else {
        setDesktopMenu(null);
        setMobileAccordion(null);
      }
    }
    if (e.key === 'Enter') {
      const first = conditionGroups[0]?.conditions[0];
      if (first) {
        e.preventDefault();
        navigate(getConditionUrl(first.slug));
      }
    }
  };

  const renderConditionList = (mobile: boolean): ReactNode => (
    <>
      {mobile && (
        <li className="ve-cond-all ve-cond-all--top">
          <Link to="/we-can-help/">See all conditions</Link>
        </li>
      )}
      <li className="ve-cond-search">
        <label htmlFor={mobile ? 'conditions-search-input-mobile' : 'conditions-search-input'} className="sr-only">
          Search conditions
        </label>
        <input
          ref={mobile ? mobileSearchRef : desktopSearchRef}
          id={mobile ? 'conditions-search-input-mobile' : 'conditions-search-input'}
          type="search"
          autoComplete="off"
          placeholder="Search conditions"
          aria-controls={mobile ? 'conditions-submenu-mobile' : 'conditions-submenu'}
          value={conditionQuery}
          onChange={(e) => setConditionQuery(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onSearchKeyDown}
        />
        {conditionQuery.trim() && (
          <span className="ve-cond-count" aria-live="polite">
            {conditionMatchCount === 0 ? 'No conditions match' : `${conditionMatchCount} match${conditionMatchCount === 1 ? '' : 'es'}`}
          </span>
        )}
      </li>
      {conditionGroups.map((group) => {
        // Phones: one collapsible row per category (matches the desktop
        // category list); a search query opens every matching group.
        const expanded = !mobile || Boolean(conditionQuery.trim()) || mobileConditionCategory === group.category;
        const listId = `conditions-mobile-${group.category}`;
        return (
        <li key={group.category} className={`ve-cond-group${mobile ? ' ve-cond-group--collapsible' : ''}${expanded ? ' is-expanded' : ''}`}>
          {mobile ? (
            <button
              type="button"
              className="ve-cond-group-toggle"
              aria-expanded={expanded}
              aria-controls={listId}
              onClick={() => setMobileConditionCategory((cur) => (cur === group.category ? null : group.category))}
            >
              <span>{group.label}</span>
              <span className="ve-cond-category-count" aria-hidden="true">{group.conditions.length}</span>
              {expanded ? <Minus aria-hidden="true" focusable="false" /> : <Plus aria-hidden="true" focusable="false" />}
            </button>
          ) : (
            <span className="ve-cond-group-label">{group.label}</span>
          )}
          <ul id={mobile ? listId : undefined} hidden={!expanded}>
            {group.conditions.map((condition) => (
              <li key={condition.slug} className={isActive(getConditionUrl(condition.slug)) ? 'is-active' : undefined}>
                <Link to={getConditionUrl(condition.slug)}>{conditionLabel(condition)}</Link>
              </li>
            ))}
          </ul>
        </li>
        );
      })}
      <li className="ve-cond-all">
        <Link to="/we-can-help/">View all conditions</Link>
      </li>
    </>
  );

  const selectDesktopConditionCategory = (category: ConditionCategory) => {
    setDesktopConditionCategory(category);
    if (conditionQuery) setConditionQuery('');
  };

  const onConditionCategoryKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = (index + 1) % allConditionGroups.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = (index - 1 + allConditionGroups.length) % allConditionGroups.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = allConditionGroups.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextGroup = allConditionGroups[nextIndex];
    selectDesktopConditionCategory(nextGroup.category);
    document.getElementById(`conditions-tab-${nextGroup.category}`)?.focus();
  };

  const renderDesktopConditionMenu = (): ReactNode => {
    const searching = Boolean(conditionQuery.trim());
    const visibleGroups = searching ? conditionGroups : activeDesktopConditionGroup ? [activeDesktopConditionGroup] : [];

    return (
      <>
        <li className="ve-cond-mega-toolbar">
          <div className="ve-cond-search">
            <label htmlFor="conditions-search-input" className="sr-only">Search conditions</label>
            <input
              ref={desktopSearchRef}
              id="conditions-search-input"
              type="search"
              autoComplete="off"
              placeholder="Search all conditions"
              aria-controls="conditions-panel"
              value={conditionQuery}
              onChange={(e) => setConditionQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={onSearchKeyDown}
            />
            {searching && (
              <span className="ve-cond-count" aria-live="polite">
                {conditionMatchCount === 0 ? 'No conditions match' : `${conditionMatchCount} match${conditionMatchCount === 1 ? '' : 'es'}`}
              </span>
            )}
          </div>
          <Link className="ve-cond-all-link" to="/we-can-help/">View all conditions <span aria-hidden="true">→</span></Link>
        </li>
        <li className="ve-cond-mega-body">
          <div className="ve-cond-categories" role="tablist" aria-label="Condition categories" aria-orientation="vertical">
            {allConditionGroups.map((group, index) => {
              const selected = group.category === activeDesktopConditionGroup?.category;
              return (
                <button
                  key={group.category}
                  id={`conditions-tab-${group.category}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="conditions-panel"
                  tabIndex={selected ? 0 : -1}
                  onMouseEnter={() => selectDesktopConditionCategory(group.category)}
                  onFocus={() => selectDesktopConditionCategory(group.category)}
                  onClick={() => selectDesktopConditionCategory(group.category)}
                  onKeyDown={(event) => onConditionCategoryKeyDown(event, index)}
                >
                  <span>{group.label}</span>
                  <span className="ve-cond-category-count" aria-hidden="true">{group.conditions.length}</span>
                </button>
              );
            })}
          </div>
          <div
            id="conditions-panel"
            className={`ve-cond-panel${searching ? ' is-searching' : ''}`}
            role="tabpanel"
            aria-labelledby={searching ? undefined : `conditions-tab-${activeDesktopConditionGroup?.category}`}
          >
            {!searching && activeDesktopConditionGroup && <h2>{activeDesktopConditionGroup.label}</h2>}
            {visibleGroups.length === 0 ? (
              <p className="ve-cond-empty">No conditions match your search.</p>
            ) : (
              visibleGroups.map((group) => (
                <section key={group.category} className="ve-cond-panel-group" aria-label={searching ? group.label : undefined}>
                  {searching && <h2>{group.label}</h2>}
                  <ul>
                    {group.conditions.map((condition) => (
                      <li key={condition.slug} className={isActive(getConditionUrl(condition.slug)) ? 'is-active' : undefined}>
                        <Link to={getConditionUrl(condition.slug)}>{conditionLabel(condition)}</Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>
        </li>
      </>
    );
  };

  const headerClass = ['ve-header', stuck ? 'is-stuck' : '', mobileOpen ? 'is-open' : ''].filter(Boolean).join(' ');

  return (
    <header ref={headerRef} className={headerClass} data-ve-header>
      {/* Utility row (desktop only) */}
      <div className="ve-utility">
        <div className="ve-container ve-container--wide ve-utility-inner">
          <ul>
            <li>
              <Link to="/blog/">Blog</Link>
            </li>
            <li>
              <Link to="/contact-us/">Contact</Link>
            </li>
            <li>
              <a href={`tel:${SITE.phone}`}>Call {SITE.phoneDisplay}</a>
            </li>
          </ul>
        </div>
      </div>

      {/* Nav row */}
      <div className="ve-navbar">
        <div className="ve-container ve-container--wide ve-navbar-inner">
          <Link to="/" className="ve-logo" aria-label="Van Every Family Chiropractic Center, home">
            <img src="/images/logo2.webp" srcSet="/images/logo2-w300.webp 300w, /images/logo2.webp 477w" sizes="(min-width: 1024px) 170px, 140px" width="477" height="185" alt="Van Every Family Chiropractic Center" />
          </Link>

          <nav className="ve-nav" aria-label="Primary">
            <ul id="primary_nav" className="ve-menu">
              {MENU.map((group) => {
                const open = desktopMenu === group.key;
                const cls = [open ? 'is-open' : '', groupActive(group) ? 'is-active' : ''].filter(Boolean).join(' ') || undefined;
                return (
                  <li key={group.key} className={cls} {...desktopHandlers(group.key)}>
                    <Link
                      to={group.to}
                      className="ve-menu-link"
                      aria-haspopup="true"
                      aria-expanded={open}
                      aria-controls={`${group.key}-submenu`}
                    >
                      {group.label}
                      <ChevronDown aria-hidden="true" focusable="false" />
                    </Link>
                    {group.key === 'conditions' ? (
                      <ul id="conditions-submenu" className="ve-submenu ve-submenu--mega">
                        {renderDesktopConditionMenu()}
                      </ul>
                    ) : (
                      <ul id={`${group.key}-submenu`} className="ve-submenu">
                        {group.children.map((item) => (
                          <li key={item.label} className={item.to && isActive(item.to) ? 'is-active' : undefined}>
                            <MenuAnchor item={item} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="ve-nav-actions">
            <button type="button" className="ve-nav-search" aria-label="Search conditions we help" onClick={openSearch}>
              <Search aria-hidden="true" focusable="false" />
            </button>
            {/* 1024-1199px shows "Webinar"; the lead words stay for screen
                readers (chrome.css .ve-nav-secondary-lead). */}
            <Link to={SECONDARY_CTA.to} className="ve-btn ve-btn--outline-navy ve-btn--sm ve-nav-secondary">
              {/* one flex item, so the space between the words survives */}
              <span>
                <span className="ve-nav-secondary-lead">{SECONDARY_CTA.lead} </span>
                {SECONDARY_CTA.short}
              </span>
            </Link>
            <Link className="ve-btn ve-btn--sm ve-nav-book" to={SITE.booking.chooserPath}>
              Book Online
            </Link>
            <Link className="ve-nav-book-icon" to={SITE.booking.chooserPath} aria-label="Book online">
              <CalendarCheck aria-hidden="true" focusable="false" />
            </Link>
            <button
              ref={toggleRef}
              type="button"
              className="ve-nav-toggle"
              aria-expanded={mobileOpen}
              aria-controls="ve-mobile-panel"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((v) => !v)}
            >
              <span>{mobileOpen ? 'Close' : 'Menu'}</span>
              {mobileOpen ? <X aria-hidden="true" focusable="false" /> : <Menu aria-hidden="true" focusable="false" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <>
          <div
            className="ve-mobile-backdrop"
            aria-hidden="true"
            style={panelTop !== null ? ({ '--ve-panel-top': `${panelTop}px` } as React.CSSProperties) : undefined}
            onClick={() => setMobileOpen(false)}
          />
          <div
            id="ve-mobile-panel"
            ref={panelRef}
            className="ve-mobile-panel"
            style={panelTop !== null ? ({ '--ve-panel-top': `${panelTop}px` } as React.CSSProperties) : undefined}
          >
            {/* Close the panel on any link tap, also when the link points at the page we are already on
                (route-change closing alone left the menu open there, so the tap looked dead). */}
            <nav
              aria-label="Mobile"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) {
                  setMobileOpen(false);
                  setMobileAccordion(null);
                }
              }}
            >
              <ul id="mobile_nav" className="ve-mobile-menu">
                <li className={isActive('/') ? 'is-active' : undefined}>
                  <div className="ve-mobile-row">
                    <Link to="/">Home</Link>
                  </div>
                </li>
                {MENU.map((group) => {
                  const expanded = mobileAccordion === group.key;
                  const subId = `${group.key}-submenu-mobile`;
                  return (
                    <li key={group.key} className={groupActive(group) ? 'is-active' : undefined}>
                      <div className="ve-mobile-row">
                        <Link to={group.to}>{group.label}</Link>
                        <button
                          type="button"
                          className="ve-mobile-toggle"
                          aria-expanded={expanded}
                          aria-controls={subId}
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
                          onClick={() => setMobileAccordion((prev) => (prev === group.key ? null : group.key))}
                        >
                          {expanded ? <Minus aria-hidden="true" focusable="false" /> : <Plus aria-hidden="true" focusable="false" />}
                        </button>
                      </div>
                      <ul id={subId} className="ve-mobile-sub" hidden={!expanded}>
                        {group.key === 'conditions'
                          ? renderConditionList(true)
                          : group.children.map((item) => (
                              <li key={item.label} className={item.to && isActive(item.to) ? 'is-active' : undefined}>
                                <MenuAnchor item={item} />
                              </li>
                            ))}
                      </ul>
                    </li>
                  );
                })}
                <li className={isActive('/blog') ? 'is-active' : undefined}>
                  <div className="ve-mobile-row">
                    <Link to="/blog/">Blog</Link>
                  </div>
                </li>
                <li className={isActive('/contact-us') ? 'is-active' : undefined}>
                  <div className="ve-mobile-row">
                    <Link to="/contact-us/">Contact Us</Link>
                  </div>
                </li>
              </ul>
            </nav>
            <div className="ve-mobile-cta">
              <Link className="ve-btn" to={SITE.booking.chooserPath}>
                Book Online
              </Link>
              <Link to={SECONDARY_CTA.to} className="ve-btn ve-btn--outline">
                {SECONDARY_CTA.label}
              </Link>
              <a className="ve-btn ve-btn--ghost" href={`tel:${SITE.phone}`}>
                <Phone aria-hidden="true" focusable="false" />
                Call {SITE.phoneDisplay}
              </a>
            </div>
            <div className="ve-mobile-meta">
              <SocialLinks />
            </div>
          </div>
        </>
      )}
    </header>
  );
}

// Compound component for split rendering in Layout
const Header = { UtilityBar, Navigation };
export default Header;
